'use client';

import React from 'react';
import * as LucideIcons from 'lucide-react';
import type { LucideProps } from 'lucide-react';

export interface IconProps {
  /** Kebab-case lucide icon name, e.g. "clock", "chevron-left". */
  name: string;
  size?: number;
  color?: string;
  strokeWidth?: number;
  fill?: string;
  'aria-hidden'?: boolean;
}

/**
 * Thin wrapper over lucide-react that renders an icon by kebab-case name.
 * Converts "chevron-left" → "ChevronLeft" and looks up the named export.
 * Falls back to rendering nothing if the name is unknown (no throw).
 */
export function Icon({
  name,
  size,
  color,
  strokeWidth,
  fill,
  'aria-hidden': ariaHidden,
}: IconProps) {
  // Convert kebab-case → PascalCase: "chevron-left" → "ChevronLeft"
  const pascalName = name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

  const IconComponent = (LucideIcons as Record<string, unknown>)[pascalName] as
    | React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>>
    | undefined;

  // lucide-react exports forwardRef components (type 'object'), not plain functions —
  // guard against null/undefined and non-React types only.
  if (
    IconComponent == null ||
    (typeof IconComponent !== 'function' && typeof IconComponent !== 'object')
  ) {
    return null;
  }

  const props: Partial<LucideProps> = {};
  if (size !== undefined) props.size = size;
  if (color !== undefined) props.color = color;
  if (strokeWidth !== undefined) props.strokeWidth = strokeWidth;
  if (fill !== undefined) props.fill = fill;
  if (ariaHidden !== undefined) props['aria-hidden'] = ariaHidden;

  return <IconComponent {...props} />;
}
